import { motion } from "framer-motion";
import {
  ArrowUpRight,
  MessageCircle,
  Gamepad2,
  Code2,
  ShieldCheck,
} from "lucide-react";

const LINKS = [
  {
    id: "roblox-group",
    label: "Roblox Group",
    description: "Join our official studio group",
    href: "https://www.roblox.com/communities/158257085/idks-Development",
    icon: Gamepad2,
    category: "Community",
    testId: "link-roblox-group",
  },
  {
    id: "discord",
    label: "Discord Server",
    description: "Hang out with the team",
    href: "https://discord.gg/RUjpugDNVr",
    icon: MessageCircle,
    category: "Community",
    testId: "link-discord",
  },
  {
    id: "apply-developer",
    label: "Developer Application",
    description: "Apply to join the dev team",
    href: "https://docs.google.com/forms/d/e/1FAIpQLSc9Cb9kwXtXH3kIOHe4uRhasAcIMBPPsC8dusgWrXjlBN_EaQ/viewform?usp=dialog",
    icon: Code2,
    category: "Applications",
    testId: "link-apply-developer",
  },
  {
    id: "apply-staff",
    label: "Staff Application",
    description: "Apply to join the staff team",
    href: "https://docs.google.com/forms/d/e/1FAIpQLScn8zCU2S--n02KJq7yaug5czvLGfE83TyvODkW8o7YydOFnQ/viewform?usp=publish-editor",
    icon: ShieldCheck,
    category: "Applications",
    testId: "link-apply-staff",
  },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.2 },
  },
};

const item = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  },
};

export const Home = () => {
  const community = LINKS.filter((l) => l.category === "Community");
  const apps = LINKS.filter((l) => l.category === "Applications");

  return (
    <main
      data-testid="home-page"
      className="relative z-10 min-h-screen flex flex-col"
    >
      {/* Nav */}
      <motion.header
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="px-6 md:px-12 lg:px-20 pt-8 flex items-center justify-between"
      >
        <div className="flex items-center gap-3" data-testid="brand">
          <div className="relative">
            <div className="w-11 h-11 rounded-xl border border-white/15 bg-white/[0.03] backdrop-blur-md overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_roblox-idk-hub/artifacts/242a2i52_image.png"
                alt="idk's development logo"
                className="w-full h-full object-cover"
                data-testid="brand-logo"
              />
            </div>
            <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-blue-300 soft-pulse" />
          </div>
          <div className="leading-tight">
            <div className="text-[11px] uppercase tracking-[0.3em] text-blue-200/60">
              Roblox Studio
            </div>
            <div className="font-semibold text-white text-sm">
              idk's development
            </div>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/40">
          <span>est. 2024</span>
        </div>
      </motion.header>

      {/* Hero */}
      <section className="flex-1 px-6 md:px-12 lg:px-20 pt-16 md:pt-24 pb-12">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="max-w-4xl"
        >
          <motion.div
            variants={item}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur text-[11px] uppercase tracking-[0.3em] text-blue-100/70 mb-8"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 float-y" />
            Building among the stars
          </motion.div>

          <motion.h1
            variants={item}
            className="title-grad font-extrabold text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95]"
            data-testid="hero-title"
          >
            idk's
            <br />
            development.
          </motion.h1>

          <motion.p
            variants={item}
            className="mt-8 max-w-xl text-white/60 text-base md:text-lg leading-relaxed"
            data-testid="hero-subtitle"
          >
            A Roblox game studio crafting playful, polished worlds. Join the
            crew, apply for a role, or just hang out — pick your portal below.
          </motion.p>

          <motion.div
            variants={item}
            className="mt-10 flex items-center gap-6 text-[11px] uppercase tracking-[0.3em] text-white/40"
          >
            <div className="flex items-center gap-2">
              <span className="w-8 h-px bg-white/30" />
              <span>scroll for links</span>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Links grid */}
      <section
        id="links"
        className="px-6 md:px-12 lg:px-20 pb-24"
        data-testid="links-section"
      >
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Community */}
          <div className="lg:col-span-5">
            <SectionHeading index="01" title="Community" />
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-100px" }}
              className="mt-6 space-y-3"
            >
              {community.map((l) => (
                <LinkCard key={l.id} link={l} variants={item} />
              ))}
            </motion.div>
          </div>

          {/* Applications */}
          <div className="lg:col-span-7">
            <SectionHeading index="02" title="Open Applications" />
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-100px" }}
              className="mt-6 grid sm:grid-cols-2 gap-3"
            >
              {apps.map((l) => (
                <LinkCard key={l.id} link={l} variants={item} />
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="px-6 md:px-12 lg:px-20 pb-8 pt-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t border-white/5"
        data-testid="footer"
      >
        <div className="text-[11px] uppercase tracking-[0.3em] text-white/40">
          © {new Date().getFullYear()} idk's development · made with stars
        </div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-white/40">
          not affiliated with roblox corp.
        </div>
      </footer>
    </main>
  );
};

const SectionHeading = ({ index, title }) => (
  <div className="flex items-baseline gap-4">
    <span className="text-[11px] uppercase tracking-[0.3em] text-blue-200/50 font-mono">
      {index}
    </span>
    <span className="text-white text-xl md:text-2xl font-semibold tracking-tight">
      {title}
    </span>
    <span className="flex-1 h-px bg-white/10" />
  </div>
);

const LinkCard = ({ link, variants }) => {
  const Icon = link.icon;
  return (
    <motion.a
      variants={variants}
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={link.testId}
      className="glass-btn group flex items-center gap-4 rounded-2xl p-4 md:p-5 text-left"
    >
      <span className="icon-wrap shrink-0">
        <Icon className="w-5 h-5 text-blue-100" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-white font-semibold text-base md:text-lg leading-tight">
          {link.label}
        </span>
        <span className="block text-white/50 text-sm mt-0.5 truncate">
          {link.description}
        </span>
      </span>
      <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-blue-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
    </motion.a>
  );
};

export default Home;
